// ORCA360 - Mobile Workflow Step Indicator
// Horizontal progress dots for the 8-step instructor recording workflow

import { Check } from 'lucide-react';

export interface WorkflowStep {
  key: string;
  label: string;
  shortLabel: string;
  icon: string;
}

export const WORKFLOW_STEPS: WorkflowStep[] = [
  { key: 'briefing', label: 'Record Briefing', shortLabel: 'Briefing', icon: '📋' },
  { key: 'simulator', label: 'Record Simulator', shortLabel: 'Simulator', icon: '✈️' },
  { key: 'speakers', label: 'Speaker Labels', shortLabel: 'Speakers', icon: '🎤' },
  { key: 'debrief-gen', label: 'Generate Debrief', shortLabel: 'Generate', icon: '⚡' },
  { key: 'debrief-read', label: 'Read Debrief', shortLabel: 'Review', icon: '📖' },
  { key: 'debriefing', label: 'Record Debriefing', shortLabel: 'Debrief', icon: '📝' },
  { key: 'final', label: 'Final Transcript', shortLabel: 'Combine', icon: '🔗' },
  { key: 'assessments', label: 'Generate Assessments', shortLabel: 'Assess', icon: '📊' },
];

interface MobileStepIndicatorProps {
  currentStep: number;
  completedSteps: Set<number>;
  unlockedSteps: Set<number>;
  onStepTap?: (step: number) => void;
}

export default function MobileStepIndicator({
  currentStep,
  completedSteps,
  unlockedSteps,
  onStepTap,
}: MobileStepIndicatorProps) {
  return (
    <div className="w-full px-2 py-1">
      {/* Dots row + label on single line */}
      <div className="flex items-center justify-center gap-0.5">
        {WORKFLOW_STEPS.map((step, idx) => {
          const isActive = idx === currentStep;
          const isCompleted = completedSteps.has(idx);
          const isUnlocked = unlockedSteps.has(idx);

          return (
            <button
              key={step.key}
              onClick={() => isUnlocked && onStepTap?.(idx)}
              disabled={!isUnlocked}
              className="flex flex-col items-center"
              aria-label={`Step ${idx + 1}: ${step.label}${isCompleted ? ' (completed)' : ''}`}
            >
              <div
                className={`flex items-center justify-center rounded-full transition-all duration-200 ${
                  isActive ? 'w-5 h-5 text-[10px] shadow-sm' : 'w-3.5 h-3.5 text-[8px]'
                } ${
                  isCompleted
                    ? 'bg-green-600 text-white'
                    : isActive
                      ? 'bg-navy text-white'
                      : isUnlocked
                        ? 'bg-slate-400 text-slate-500'
                        : 'bg-slate-200 text-slate-500'
                }`}
              >
                {isCompleted ? (
                  <Check size={isActive ? 10 : 8} strokeWidth={3} />
                ) : (
                  <span className="font-bold">{idx + 1}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Current step label */}
      <div className="text-center">
        <span className="text-[10px] font-semibold text-navy">
          {WORKFLOW_STEPS[currentStep]?.icon} {WORKFLOW_STEPS[currentStep]?.label}
        </span>
      </div>
    </div>
  );
}
